/** Chapter 12, creators and community: creators by job, the brand's own people (30). */
import type { StrategyCreators } from "@/types/strategyDocument.type";
import {
  Eyebrow, Foot, Headline, Picture, Slide, SourceLine, V,
  accountName, countWord, countryName, platformName, slideText,
  type TemplateContext,
} from "./kit";

type Creator = StrategyCreators["list"][number];

const ROLES: Record<Creator["role"], string> = { reach: "Reach", trust: "Trust", making: "Making" };
const FOUND_BY: Record<Creator["found_by"], string> = {
  tiktok_popular_list: "TikTok popular lists",
  set_partner_tags: "partner tags",
  name_search: "name search",
  client: "the client’s list",
};

export function CreatorsSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const creators = doc.creators!;
  const t = slideText(ctx);
  const n = creators.list.length;
  const found = (c: Creator) => {
    switch (c.found_by) {
      case "tiktok_popular_list": return "Found on TikTok’s popular list.";
      case "set_partner_tags": return `Found in ${c.found_via ? `${accountName(doc, ctx.brand, c.found_via)}’s` : "the set’s"} partner tags.`;
      case "name_search": return "Found by name search.";
      case "client": return "From the client’s list.";
    }
  };
  const ways = [...new Set(creators.list.filter((c) => c.found_by !== "client").map((c) => FOUND_BY[c.found_by]))];
  const fallback = `${countWord(n).replace(/^./, (c) => c.toUpperCase())} creator${n === 1 ? "" : "s"} *the audience already follows.*`;
  return (
    <Slide ink>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback={fallback} size={54} lineHeight={60} className="sv-hl-regular" />
        <div className="sv-creators">
          {creators.list.length ? (
            <div className="sv-creators-block">
              <p className="sv-creators-head">Creators, by the job they do</p>
              <div className="sv-creators-row">
                {creators.list.slice(0, 4).map((c) => {
                  const audience = c.audience_fit?.countries.length
                    ? `Audience: ${c.audience_fit.countries.map((x) => `${countryName(x.country)} ${x.share}%`).join(", ")}.`
                    : "Audience not public.";
                  return (
                    <div key={c.id} className="sv-creator">
                      <Picture ctx={ctx} slot={`creator:${c.id}`} label={`${c.name}’s picture`} url={c.picture_url} className="sv-creator-pic" />
                      <div className="sv-creator-words">
                        <V as="p" className="sv-creator-name">{c.name}</V>
                        <p className="sv-creator-handle"><V>@{c.handle.replace(/^@/, "")}</V> · {platformName(c.platform)}</p>
                        <p className="sv-creator-with"><V>{audience}</V> <V>{found(c)}</V></p>
                        <p className="sv-creator-best">
                          <V>{ROLES[c.role]}</V> · {c.licence?.status === "confirmed_by_client" ? "licence confirmed by the client" : <span className="sv-open">licence unknown, to confirm</span>}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          {creators.own_people.length ? (
            <div className="sv-own">
              <p className="sv-creators-head">The brand’s own people</p>
              {creators.own_people.slice(0, 4).map((p) => (
                <div key={p.id} className="sv-own-row">
                  <V as="p" className="sv-own-name">{p.name}</V>
                  <p className="sv-own-kind"><V>{p.role}</V></p>
                  {p.what ? <V as="p" className="sv-own-what">{p.what}</V> : null}
                </div>
              ))}
            </div>
          ) : null}
          {t.reading ? <V as="p" className="sv-creators-note">{t.reading}</V> : null}
        </div>
        <Foot ctx={ctx} flag={ways.length ? <SourceLine className="sv-flag-line"><V>{ways.join(", ").replace(/^./, (c) => c.toUpperCase())}</V> · licences: the client</SourceLine> : null} />
      </div>
    </Slide>
  );
}
